import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  NgModule,
  Output,
  QueryList,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SD_OPTION_PARENT, SdOption, SdOptionParent} from './option';
import {SdOptionModule} from './core';

export class SdSelectChange {
  constructor(public source: SdSelect, public value: unknown) {}
}

@Component({
  selector: 'sd-select',
  standalone: true,
  imports: [],
  template: `
    <button
      type="button"
      class="flex w-full items-center justify-between bg-transparent py-1 text-left text-sm outline-none disabled:opacity-50"
      [disabled]="disabled"
      (click)="toggle($event)"
    >
      <span class="truncate" [class.text-[var(--m365-text-secondary,#6b6b70)]]="empty">
        {{ displayLabel || placeholder || '' }}
      </span>
      <span class="material-icons text-lg text-[var(--m365-text-secondary,#6b6b70)]">expand_more</span>
    </button>
    @if (open) {
      <div class="sd-select-panel absolute left-0 right-0 z-[80] mt-1 max-h-60 overflow-auto rounded-lg border border-[var(--m365-border-rest,#ececf0)] bg-white py-1 shadow-lg">
        <ng-content></ng-content>
      </div>
    }
  `,
  host: {class: 'sd-select-host sd-select relative block w-full'},
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SdSelect), multi: true},
    {provide: SD_OPTION_PARENT, useExisting: forwardRef(() => SdSelect)},
  ],
})
export class SdSelect implements ControlValueAccessor, AfterContentInit, SdOptionParent {
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() required = false;
  @Input() panelClass: string | string[] | undefined;
  @Output() selectionChange = new EventEmitter<SdSelectChange>();
  @Output() valueChange = new EventEmitter<unknown>();
  @ContentChildren(SdOption, {descendants: true}) options!: QueryList<SdOption>;

  open = false;
  displayLabel = '';
  private inner: unknown;
  private onChange: (v: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  @Input()
  get value(): unknown {
    return this.inner;
  }
  set value(v: unknown) {
    this.writeValue(v);
  }

  get empty(): boolean {
    return this.inner == null || this.inner === '' || (Array.isArray(this.inner) && this.inner.length === 0);
  }

  ngAfterContentInit(): void {
    this.refreshLabel();
    this.options?.changes.subscribe(() => this.refreshLabel());
  }

  writeValue(v: unknown): void {
    this.inner = v;
    this.refreshLabel();
  }

  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
  }

  selectOption(option: SdOption): void {
    if (this.multiple) {
      const current = Array.isArray(this.inner) ? [...this.inner] : [];
      const idx = current.findIndex((v) => v === option.value);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(option.value);
      }
      this.commit(current);
    } else {
      this.commit(option.value);
      this.open = false;
    }
  }

  isOptionSelected(option: SdOption): boolean {
    if (this.multiple) {
      return Array.isArray(this.inner) && this.inner.includes(option.value);
    }
    return this.inner === option.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.onTouched();
    }
  }

  private commit(next: unknown): void {
    this.inner = next;
    this.refreshLabel();
    this.onChange(next);
    this.onTouched();
    this.valueChange.emit(next);
    this.selectionChange.emit(new SdSelectChange(this, next));
  }

  private refreshLabel(): void {
    const opts = this.options?.toArray() ?? [];
    if (this.multiple && Array.isArray(this.inner)) {
      this.displayLabel = opts
        .filter((o) => (this.inner as unknown[]).includes(o.value))
        .map((o) => this.labelOf(o))
        .join(', ');
      return;
    }
    const match = opts.find((o) => o.value === this.inner);
    this.displayLabel = match ? this.labelOf(match) : (this.inner == null ? '' : String(this.inner));
  }

  private labelOf(option: SdOption): string {
    return option.viewValue || String(option.value ?? '');
  }
}

@NgModule({
  imports: [SdSelect, SdOptionModule],
  exports: [SdSelect, SdOptionModule],
})
export class SdSelectModule {}
